// ./src/utils/api/UserNotificationService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const USER_NOTIFICATION_FIELDS = `
  id
  accountId
  userId
  carId
  entityId
  entityUid
  createdAt
  readAt
  notificationType
  message
  sender
  actionInfo
  user {
    id
    firstName
    lastName
    email
  }
  car {
    id
    label
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const USER_NOTIFICATION_LIST_QUERY = gql`
  query UserNotificationList(
    $filter: UserNotificationFilter
    $params: PaginationAndSorting
  ) {
    userNotificationList(filter: $filter, params: $params) {
      code
      data {
        ${USER_NOTIFICATION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_NOTIFICATION_GET_QUERY = gql`
  query UserNotificationGet($id: ID) {
    userNotificationGet(id: $id) {
      code
      data {
        ${USER_NOTIFICATION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_NOTIFICATION_GET_MANY_QUERY = gql`
  query UserNotificationGetMany($ids: [ID]) {
    userNotificationGetMany(ids: $ids) {
      code
      data {
        ${USER_NOTIFICATION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const USER_NOTIFICATION_CREATE_MUTATION = gql`
  mutation UserNotificationCreate($params: UserNotificationInput) {
    userNotificationCreate(params: $params) {
      code
      data {
        ${USER_NOTIFICATION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_NOTIFICATION_UPDATE_MUTATION = gql`
  mutation UserNotificationUpdate(
    $where: UserNotificationWhereInput
    $params: UserNotificationInput
  ) {
    userNotificationUpdate(where: $where, params: $params) {
      code
      data {
        ${USER_NOTIFICATION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_NOTIFICATION_REMOVE_MUTATION = gql`
  mutation UserNotificationRemove($where: UserNotificationWhereInput) {
    userNotificationRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_NOTIFICATION_REMOVE_MANY_MUTATION = gql`
  mutation UserNotificationRemoveMany($where: [UserNotificationWhereInput]) {
    userNotificationRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface UserNotification {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;
  entityId?: string | undefined;
  entityUid?: string | undefined;
  createdAt?: string | undefined;
  readAt?: string | undefined;
  notificationType?: number | undefined;
  message?: string | undefined;
  sender?: string | undefined;
  actionInfo?: string | undefined;
  user?: User | undefined;
  car?: Car | undefined;
}

export interface UserNotificationInput {
  id?: string | null | undefined;
  carId?: string | null | undefined;
  entityId?: string | null | undefined;
  entityUid?: string | null | undefined;
  readAt?: string | null | undefined;
  notificationType?: number | null | undefined;
  message?: string | null | undefined;
  sender?: string | null | undefined;
  actionInfo?: string | null | undefined;
}

export interface UserNotificationFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  notificationType?: number[] | null | undefined;
  entityId?: string[] | null | undefined;
  isRead?: boolean | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface UserNotificationWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * UserNotification service that handles all user notification-related API calls.
 * Used for in-app notifications like maintenance reminders, account sharing invites, etc.
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class UserNotificationService {
  /**
   * Get a list of user notifications with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: UserNotificationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_NOTIFICATION_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single user notification by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_NOTIFICATION_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple user notifications by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      USER_NOTIFICATION_GET_MANY_QUERY,
      {
        ids,
      }
    );
    return result;
  }

  /**
   * Create a new user notification
   */
  static async create(params: UserNotificationInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_NOTIFICATION_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing user notification (e.g., mark as read)
   */
  static async update(
    where: UserNotificationWhereInput,
    params: UserNotificationInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_NOTIFICATION_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a user notification
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_NOTIFICATION_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple user notifications
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      USER_NOTIFICATION_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Mark a notification as read
   */
  static async markAsRead(id: string): Promise<OpResult> {
    return UserNotificationService.update(
      { id },
      { readAt: new Date().toISOString() }
    );
  }

  /**
   * Mark multiple notifications as read
   */
  static async markManyAsRead(ids: string[]): Promise<OpResult[]> {
    const results = await Promise.all(
      ids.map((id) => UserNotificationService.markAsRead(id))
    );
    return results;
  }
}